import Markdown from '@/components/atoms/Markdown/Markdown';
import VideoPlayer from '@/modules/content/common/VideoPlayer';
import { TContentDetail } from '@/types/Content.types';
import { Image, Text, VStack } from '@chakra-ui/react';
import React from 'react';

// Read-only render of a content item as it appears to readers. Generic across
// creator + platform content (no MyOmnia-specific branding).

type RawSub = { _id?: string; title?: string; text?: string; imageUrl?: string | null };
type RawSection = RawSub & { subsections?: RawSub[] };
type RawDetails = {
  body?: string;
  sections?: RawSection[];
  source?: string;
  fullDescription?: string;
  mux?: { playbackId?: string; status?: string; thumbnailUrl?: string };
  youtube?: { videoId?: string };
};

const ContentView = ({ content }: { content: TContentDetail }) => {
  const details = (content.details ?? {}) as RawDetails;
  const cover = content.effectiveThumbnailUrl;

  return (
    <VStack align="stretch" width="100%" maxWidth="760px" gap="18px">
      {/* Hero */}
      {content.type === 'video' ? (
        <VideoPlayer
          source={details.source === 'youtube' ? 'youtube' : 'mux'}
          youtubeVideoId={details.youtube?.videoId}
          muxStatus={details.mux?.status}
          muxPlaybackId={details.mux?.playbackId}
          posterUrl={cover || details.mux?.thumbnailUrl}
          title={content.title}
        />
      ) : (
        cover && (
          <Image
            src={cover}
            alt={content.title || 'Cover image'}
            width="100%"
            maxHeight="360px"
            objectFit="cover"
            borderRadius="12px"
          />
        )
      )}

      <VStack align="stretch" gap="6px">
        <Text fontSize="28px" fontWeight={700} color="text.darkBlue" fontFamily="Lora">
          {content.title || 'Untitled'}
        </Text>
        {/* A post carries the same text in description and body, so the body below is the
            one that renders it — in full, as markdown. Showing it here too would repeat
            the same text directly above itself. */}
        {content.type !== 'post' && content.description && (
          <Text fontSize="16px" color="text.mediumBlue">
            {content.description}
          </Text>
        )}
      </VStack>

      {/* Body */}
      {content.type === 'post' && <Markdown>{details.body || '_No content._'}</Markdown>}

      {content.type === 'video' && details.fullDescription && (
        <Text fontSize="15px" color="text.darkBlue" whiteSpace="pre-wrap">
          {details.fullDescription}
        </Text>
      )}

      {content.type === 'article' &&
        (details.sections ?? []).map((section, index) => (
          <VStack key={section._id ?? index} align="stretch" gap="8px">
            {section.title && (
              <Text fontWeight={700} fontSize="18px" color="text.darkBlue">
                {section.title}
              </Text>
            )}
            <Markdown>{section.text || ''}</Markdown>
            {section.imageUrl && (
              <Image src={section.imageUrl} alt={section.title || `Section ${index + 1}`} borderRadius="8px" />
            )}
            {(section.subsections ?? []).map((sub, subIndex) => (
              <VStack key={sub._id ?? subIndex} align="stretch" gap="6px" paddingLeft="12px">
                {sub.title && (
                  <Text fontWeight={700} fontSize="15px" color="text.mediumBlue">
                    {sub.title}
                  </Text>
                )}
                <Markdown>{sub.text || ''}</Markdown>
                {sub.imageUrl && <Image src={sub.imageUrl} alt={sub.title || 'Subsection'} borderRadius="8px" />}
              </VStack>
            ))}
          </VStack>
        ))}
    </VStack>
  );
};

export default ContentView;
